import { AlertTriangle, Check, ChevronDown, ChevronRight, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useScaffoldEvents } from "@/hooks/use-scaffold-events";

const BASE_STEPS = [
  { key: "creating_repo", label: "Creating GitHub repository" },
  { key: "scaffolding", label: "Running scaffold template" },
  { key: "preparing", label: "Installing dependencies" },
] as const;

const PERSONALIZE_STEP = { key: "personalizing", label: "Personalizing project" } as const;

const SUPABASE_STEP = { key: "setting_up_supabase", label: "Setting up Supabase" } as const;

const PUSH_STEP = { key: "pushing", label: "Pushing to GitHub" } as const;

const RAILWAY_STEP = { key: "setting_up_railway", label: "Deploying to Railway" } as const;

const INSTRUCTIONS_STEP = {
  key: "generating_instructions",
  label: "Creating setup instructions",
} as const;

type PipelineStep =
  | (typeof BASE_STEPS)[number]
  | typeof PERSONALIZE_STEP
  | typeof SUPABASE_STEP
  | typeof PUSH_STEP
  | typeof RAILWAY_STEP
  | typeof INSTRUCTIONS_STEP;

function getSteps(
  hasPersonalization: boolean,
  hasSupabase: boolean,
  hasRailway: boolean,
): PipelineStep[] {
  const steps: PipelineStep[] = [...BASE_STEPS];
  if (hasPersonalization) {
    steps.push(PERSONALIZE_STEP);
  }
  if (hasSupabase) {
    steps.push(SUPABASE_STEP);
  }
  steps.push(PUSH_STEP);
  if (hasRailway) {
    steps.push(RAILWAY_STEP);
  }
  steps.push(INSTRUCTIONS_STEP);
  return steps;
}

export interface CompletionData {
  githubUrl?: string;
  supabaseUrl?: string;
  railwayUrl?: string;
  appUrl?: string;
  railwayAppUrl?: string;
  message?: string;
  hasWarnings: boolean;
}

interface ScaffoldProgressProps {
  projectId: number;
  onComplete: (data: CompletionData) => void;
  onFailed: () => void;
  includePersonalization?: boolean;
  includeSupabase?: boolean;
  includeRailway?: boolean;
}

/** Collapsible terminal panel for a single step. */
export function StepLogs({
  lines,
  open,
  onToggle,
}: {
  lines: string[];
  open: boolean;
  onToggle: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!open || !el) {
      return;
    }
    // Only auto-scroll if the user is already near the bottom (within 40px).
    // This prevents new log lines from yanking focus away when the user is
    // browsing earlier steps.
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (isNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines.length, open]);

  if (lines.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 ml-9 overflow-hidden rounded border">
      <button
        type="button"
        onClick={onToggle}
        className="bg-muted/40 hover:bg-muted flex w-full items-center gap-1.5 px-2 py-1 text-left text-[11px] transition-colors"
      >
        {open ? (
          <ChevronDown className="text-muted-foreground size-3" />
        ) : (
          <ChevronRight className="text-muted-foreground size-3" />
        )}
        <span className="text-muted-foreground font-medium">
          {lines.length} line{lines.length !== 1 && "s"}
        </span>
      </button>
      {open && (
        <div
          ref={scrollRef}
          className="max-h-64 overflow-auto bg-[#11262B] px-2.5 py-1.5 font-mono text-[11px] leading-[18px] text-white/80"
        >
          {lines.map((line, i) => (
            <div key={i} className="break-all">
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ScaffoldProgress({
  projectId,
  onComplete,
  onFailed,
  includePersonalization,
  includeSupabase,
  includeRailway,
}: ScaffoldProgressProps) {
  const { events, logsByStep, done, error } = useScaffoldEvents(projectId);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  const hasPersonalization =
    includePersonalization ||
    events.some(
      (e) =>
        e.step === "personalizing" ||
        e.step === "personalizing_done" ||
        e.step === "personalizing_failed",
    );
  const hasSupabase =
    includeSupabase ||
    events.some((e) => e.step === "setting_up_supabase" || e.step === "setting_up_supabase_done");
  const hasRailway =
    includeRailway ||
    events.some(
      (e) =>
        e.step === "setting_up_railway" ||
        e.step === "setting_up_railway_done" ||
        e.step === "setting_up_railway_failed",
    );
  const STEPS = getSteps(hasPersonalization, hasSupabase, hasRailway);

  const completedSteps = new Set(
    events.filter((e) => e.step?.endsWith("_done")).map((e) => e.step!.replace("_done", "")),
  );

  const failedSteps = new Set(
    events.filter((e) => e.step?.endsWith("_failed")).map((e) => e.step!.replace("_failed", "")),
  );

  // Track all steps that have started but not yet completed/failed.
  // This handles parallel steps (e.g. personalizing + setting_up_supabase).
  const activeSteps = useMemo(
    () =>
      new Set(
        events
          .filter(
            (e) =>
              e.type === "step" &&
              e.step &&
              !e.step.endsWith("_done") &&
              !e.step.endsWith("_failed"),
          )
          .map((e) => e.step!)
          .filter((step) => !completedSteps.has(step) && !failedSteps.has(step)),
      ),

    [events],
  );

  const completedEvent = events.find((e) => e.type === "completed");

  // Transition to the complete step when scaffold finishes successfully
  const completedRef = useRef(false);
  useEffect(() => {
    if (!done || completedRef.current) {
      return;
    }
    completedRef.current = true;
    if (error) {
      onFailed();
    } else if (completedEvent) {
      onComplete({
        githubUrl: completedEvent.github_url,
        supabaseUrl: completedEvent.supabase_url,
        railwayUrl: completedEvent.railway_url,
        appUrl: completedEvent.app_url,
        railwayAppUrl: completedEvent.railway_app_url,
        message: completedEvent.message,
        hasWarnings: failedSteps.size > 0,
      });
    }
  }, [done, error, completedEvent, failedSteps.size, onComplete, onFailed]);

  // Auto-expand active steps when they start receiving logs.
  // Preserve the parent scroll container's position when the user has scrolled
  // up to browse earlier steps — only auto-scroll if already near the bottom.
  useEffect(() => {
    const toExpand = [...activeSteps].filter(
      (step) => logsByStep[step]?.length && !expandedSteps.has(step),
    );
    if (toExpand.length > 0) {
      // Find the nearest scrollable ancestor (DialogBody)
      const scrollParent = containerRef.current?.closest("[data-slot='dialog-body']");
      const wasNearBottom =
        scrollParent &&
        scrollParent.scrollHeight - scrollParent.scrollTop - scrollParent.clientHeight < 60;

      // Capture scroll position before expanding
      const prevScrollTop = scrollParent?.scrollTop ?? 0;

      // Auto-expand newly active steps. The setState is intentional during
      // streaming progress — it reflects derived UI state from incoming SSE
      // events and only fires when toExpand is non-empty.

      setExpandedSteps((prev) => new Set([...prev, ...toExpand]));

      // If the user was NOT near the bottom, restore their scroll position
      // after React re-renders. If they were near the bottom, let it scroll.
      if (scrollParent && !wasNearBottom) {
        requestAnimationFrame(() => {
          scrollParent.scrollTop = prevScrollTop;
        });
      }
    }
  }, [activeSteps, logsByStep, expandedSteps]);

  function toggleStep(key: string) {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <div ref={containerRef} className="space-y-4">
      <div className="space-y-1">
        {STEPS.map((step) => {
          const isComplete = completedSteps.has(step.key);
          const isStepFailed = failedSteps.has(step.key);
          const isActive = activeSteps.has(step.key) && !isComplete && !isStepFailed;
          const isFailed = (error && activeSteps.has(step.key)) || isStepFailed;
          const stepLogs = logsByStep[step.key] ?? [];

          return (
            <div key={step.key}>
              <div className="flex items-center gap-3 py-1.5">
                <div className="flex size-6 shrink-0 items-center justify-center">
                  {isComplete ? (
                    <Check className="size-5 text-green-600 dark:text-green-500" />
                  ) : isStepFailed ? (
                    <AlertTriangle className="size-5 text-amber-500" />
                  ) : isFailed ? (
                    <X className="size-5 text-red-600" />
                  ) : isActive ? (
                    <Loader2 className="text-accent size-5 animate-spin" />
                  ) : (
                    <div className="bg-muted size-2 rounded-full" />
                  )}
                </div>
                <span
                  className={`text-sm ${
                    isComplete
                      ? "text-green-700 dark:text-green-500"
                      : isStepFailed
                        ? "text-amber-600 dark:text-amber-400"
                        : isActive
                          ? "text-foreground font-medium"
                          : "text-muted-foreground"
                  }`}
                >
                  {step.label}
                </span>
              </div>

              <StepLogs
                lines={stepLogs}
                open={expandedSteps.has(step.key)}
                onToggle={() => toggleStep(step.key)}
              />
            </div>
          );
        })}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}
    </div>
  );
}
